<!-- MILKY WAY WIDGET IS CUSTOM!-->
<!-- DO NOT SYNC -->

<template>
  <BOffcanvas
      v-model="model"
      class="digi-booking"
      :placement="windowWidth < 992 ? 'bottom' : 'end'"
      :title="title"
      footer-class="footer"
      @close="close"
  >
    <template #header>
      <button
          v-if="navLevel > 0"
          class="btn-close back"
          @click="goBack"
      >
        <i class="fa-regular fa-arrow-left"></i>
      </button>
      <h5 class="offcanvas-title">
        {{ truncatedTitle }}
      </h5>
      <BCloseButton @click="close"/>
    </template>

    <BOverlay :show="initialising || loading.sessions || loading.events || loading.tickets">
      <div v-if="!selectedDate">
        <SessionCalendar
            :key="JSON.stringify(sessionDates)"
            :min-date="minDate || new Date()"
            :max-date="maxDate || new Date()"
            :calendar-dates="calendarDates"
            :use-calendar-dates="useCalendarDates"
            :session-dates="sessionDates"
            :calendar-bg-colour="calendarBgColour"
            :calendar-text-colour="calendarTextColour"
            :layout="calendarLayout"
            :custom-calendar-title="customCalendarTitle"
            :disable-calendar-scroll="disableCalendarScroll"
            :custom-dates="customDates"
            :show-sold-out-dates-object="showSoldOutDatesObject"
            @day-click="selectDate"
        />

        <Products
            v-if="otherProducts.length"
            :products="otherProducts"
            :titleText="otherProductsText"
            :buttonBG="buttonBgColour"
            :buttonText="buttonTextColour"
            :buttonSecondColor="secondButtonTextColour"
            :buttonRadius="buttonRadius + 'px'"
            :buttonStyle="buttonTextClass"
        />
      </div>
      <template v-else-if="eventsForDate.length && !choosingSession">
        <h5 class="w-full mb-4 mt-2">{{ chooseYourEventText }}</h5>
        <EventCard
            v-for="(e, i) in eventsForDate"
            :key="`${e['eventCatID']}-${i}`"
            :event="e"
            :tickets="tickets"
            :buttonBG="buttonBgColour"
            :selected="e.eventCatID === selectedEvent?.eventCatID"
            :show-pricing="e.eventCatID === selectedEvent?.eventCatID && e.sessionType === 'Date-based'"
            :singleSessionTimeEnabled="singleSessionTimeEnabled"
            @click="selectEvent(e)"
        />
      </template>
      <template v-else-if="choosingSession">
        <SessionPicker
            v-model="selectedDate"
            :event="selectedEvent"
            :min-date="minDate"
            :max-date="maxDate"
            :buttonBG="buttonBgColour"
            :buttonText="buttonTextColour"
            :low-capacity-threshold="props.lowCapacityThreshold"
            :tickets="tickets"
            :session-availability-map="sessionAvailabilityMap"
            :loading-availability="loadingAvailability"
            @session-chosen="selectedSession = $event"
            @date-changed="onDateChange"
            :sale-cutoff-options="saleCutoffOptions"
            :show-session-availability="showSessionAvailability"
            :custom-sold-out-threshold="customSoldOutThreshold"
        />
      </template>
    </BOverlay>
    <template #footer>
      <div class="d-flex text-start flex-column footer position-relative">
        <div v-if="navLevel === 0"  class="text-center w-100 pb-3"><img src="../assets/powered-by-digitickets.svg" class="powered-by w-100" alt="Digitickets"/></div>
        <div v-if="navLevel !== 0" class="booking-date-button">
          <div
              v-if="choosingSession"
              class="row mx-0 justify-content-start align-items-center"
          >
            <div class="col confirmation pb-3 px-2">
              <i class="mx-2 fal fa-calendar-check"></i>
              {{ bookingDateText }}
            </div>
          </div>
          <BButton
              v-if="!selectedEvent"
              class="m-2 mb-0"
              block
              disabled
              :style="{background: '#ccc', color: '#fff', borderRadius: buttonRadius + 'px' }">
            CONTINUE <i class="fa-regular fa-arrow-right ms-2"></i>
          </BButton>
          <BButton
              v-if="selectedEvent"
              class="m-2 mb-0"
              :href="selectedSession?.cutoffStatus === 'direct_to_phone' && props.saleCutoffOptions?.phone
                ? `tel:${props.saleCutoffOptions.phone}`
                : selectedSession
                  ? bookingHref
                  : null"
              target="_blank"
              :style="{background: buttonBgColour, color: buttonTextColour, borderRadius: buttonRadius + 'px' }"
              :class="buttonTextClass"
              block
              :disabled="(!selectedSession && choosingSession) || loadingAvailability"
              @click="selectedSession ? undefined : handleBookingClick()"
          >
            <span v-if="loadingAvailability">
              <i class="fas fa-spinner fa-spin me-2"></i>Checking...
            </span>
            <span v-else>
              <template v-if="selectedSession?.cutoffStatus === 'direct_to_phone'">
                Call {{ props.saleCutoffOptions.phone }} to enquire
                <i class="fa-solid fa-phone ms-1"></i>
              </template>
              <template v-else>
                {{ selectedEventRequiresTimePicker && !selectedSession && !choosingSession ? 'Select Time' : 'Book Now' }}
                <i class="fa-regular fa-arrow-right ms-2"></i>
              </template>
            </span>
          </BButton>
        </div>
      </div>
    </template>
  </BOffcanvas>
</template>

<script setup>
import {computed, onMounted, reactive, ref, watch} from "vue";
import moment from "moment";
import {bookingLink} from "../functions/linkFunctions";
import {sessionStartTime} from "../functions/sessionFunctions";
import SessionCalendar from "./SessionCalendar.vue";
import Products from "./Products.vue";
import EventCard from "./EventCard.vue";
import SessionPicker from "./SessionPicker.vue";
import {buildUri} from "../functions/urlFunctions";

const model = defineModel();

const props = defineProps({
  whenWouldYouLikeText: {
    type: String,
    required: true,
  },
  chooseYourEventText: {
    type: String,
    required: true,
  },
  otherProductsText: {
    type: String,
    required: true,
  },
  calendarBgColour: {
    type: String,
    required: true,
  },
  calendarTextColour: {
    type: String,
    required: true,
  },
  buttonBgColour: {
    type: String,
    required: true,
  },
  buttonTextColour: {
    type: String,
    required: true,
  },
  secondButtonTextColour: {
    type: String,
    required: true,
  },
  buttonRadius: {
    type: String,
    required: true,
  },
  buttonTextStyle: {
    type: String,
    required: true,
  },
  cfDomain: {
    type: String,
    required: true,
  },
  branchId: {
    type: Number,
    default: null,
    required: false,
  },
  lowCapacityThreshold: {
    type: Number,
    default: null,
    required: false,
  },
  otherProducts: {
    type: Array,
    default: null,
    required: false,
  },
  useCalendarDates: {
    type: [Boolean, Number],
    default: false,
    required: false,
  },
  calendarDates: {
    type: Array,
    default: [],
    required: false,
  },
  eventIds: {
    type: Array,
    required: false,
  },
  singleSessionTimeEnabled: {
    type: Boolean,
    default: false,
  },
  checkSalesLimitEnabled: {
    type: Boolean,
    default: false,
  },
  calendarLayout: {
    type: String,
    default: 'default'
  },
  customCalendarTitle: {
    type: String,
    required: false,
  },
  disableCalendarScroll: {
    type: Boolean,
    default: false
  },
  customDates: {
    type: Object,
    required: false,
    default: null
  },
  customSoldOutThreshold: {
    type: Number,
    default: null,
    required: false,
  },
  saleCutoffOptions: {
    type: Object,
    required: false,
    default: () => ({
      behaviour: 'do_not_show',
      message: null,
      phone: null,
    }),
  },
  showSessionAvailability: {
    type: Boolean,
    default: false
  },
  showSoldOutDatesOnCalendar: {
    type: Boolean,
    default: false
  }
});

const selectedDate = ref(null);
const selectedEvent = ref(null);
const selectedSession = ref(null);
const navLevel = ref(0);
const choosingSession = ref(false);

const customEventIds = ref(null);
const initialising = ref(true);
const loading = reactive({
  events: false,
  sessions: false,
  tickets: false,
});

// API Data
const events = ref([]);
const tickets = ref([]);
// Delay user ability to click through to session picker before sales limit availability data is loaded
const loadingAvailability = ref(false);

onMounted(async () => {
  // Provide a mechanism to open the offcanvas from outside Vue
  if (window.vueAppController) {
    window.vueAppController.vueInstance = {openOffcanvas};
  }

  loading.sessions = true;
  loading.events = true;
  loading.tickets = true;

  // Fetch events, sessions & tickets
  await Promise.all([
    fetchEvents(),
    fetchTickets(),
  ]).then(async () => {
    await fetchSessions();
  });

  initialising.value = false;
});

watch(customEventIds, async () => {
  loading.sessions = true;
  loading.events = true;
  loading.tickets = true;

  await Promise.all([
    fetchEvents(),
    fetchTickets(),
  ]).then(async () => {
    await fetchSessions();
  })
});

// const title = computed(() => (selectedDate.value ? moment(selectedDate.value).format('Do MMMM YYYY') : props.whenWouldYouLikeText));

const buttonTextClass = computed(() => {
  if (props.buttonTextStyle === "1") {
    return "text-uppercase";
  } else if (props.buttonTextStyle === "2") {
    return "tex t-capitalize";
  }
  return ""; // Default case (no specific style)
});

const truncatedTitle = computed(() => {
  const value = title.value;
  const limit = 35;
  return value.length > limit ? value.substring(0, limit) + '…' : value;
});

const title = computed(() => {
  if (selectedEvent.value && choosingSession.value) {
    return selectedEvent.value.name;
  }
  return selectedDate.value
      ? moment(selectedDate.value).format('Do MMMM YYYY')
      : props.whenWouldYouLikeText;
});

const selectedEventRequiresTimePicker = computed(() => selectedEvent.value.sessionType === 'Time-based' || selectedEvent.value.sessions.length > 1)
const windowWidth = computed(() => window.innerWidth)

// CHECKS AGAINST CUSTOM SOLD OUT THRESHOLD
const eventsForDate = computed(() => {
  // Returns 0 (default) if not a number or not set
  const soldOutThreshold = isNaN(Number(props.customSoldOutThreshold)) ? 0 : Number(props.customSoldOutThreshold);
  const eventsWithSessionsForDate = events.value.map(
      (e) => ({
        ...e,
        allSessions: e.sessions,
        sessions: e.sessions.filter(
            (s) => moment(new Date(s['date'])).isSame(moment(selectedDate.value), 'date')
        ),
      })
  );

  return eventsWithSessionsForDate.filter((e) => e.sessions.length && e.sessions.some((s) => s.spacesAvailable > soldOutThreshold)).sort((a, b) => a.navOrder - b.navOrder);
});

// CHECKS AGAINST CUSTOM SOLD OUT THRESHOLD
const sessionDates = computed(() => {
  // Returns 0 (default) if not a number or not set
  const soldOutThreshold = isNaN(Number(props.customSoldOutThreshold)) ? 0 : Number(props.customSoldOutThreshold);
  const timestamps = events.value.flatMap(e =>
      e.sessions
          .filter(s => s.spacesAvailable > soldOutThreshold)
          .map(s => {
            const dt = new Date(s.date)
            dt.setHours(0, 0, 0, 0)
            return dt.getTime()
          })
  )

  return Array.from(new Set(timestamps))
      .sort((a, b) => a - b)
      .map(ts => new Date(ts))
})

const minDate = computed(() => sessionDates.value.at(0));
const maxDate = computed(() => sessionDates.value.at(-1));

const bookingDateText = computed(() => {
  if (!selectedDate.value) {
    return '';
  }

  const formattedDate = moment(selectedDate.value).format('Do MMMM YYYY');

  return selectedSession.value
      ? formattedDate + ' @ ' + moment(sessionStartTime(selectedSession.value)).format('HH:mm')
      : formattedDate
})

// SHOWING SOLD OUT DATES ON CALENDAR:
// Dates that do have sessions, but none are above the sold-out threshold
const soldOutDates = computed(() => {
  const soldOutThreshold = isNaN(Number(props.customSoldOutThreshold)) ? 0 : Number(props.customSoldOutThreshold);

  // Group all sessions by (normalised) date
  const groups = new Map(); // key: timestamp @00:00, value: array of sessions
  events.value.forEach(e => {
    e.sessions.forEach(s => {
      const dt = new Date(s.date);
      dt.setHours(0, 0, 0, 0);
      const key = dt.getTime();
      const arr = groups.get(key) || [];
      arr.push(s);
      groups.set(key, arr);
    });
  });

  // Include a date if it has >=1 session AND every session is at/below the threshold
  return Array.from(groups.entries())
      .filter(([, sessions]) =>
          sessions.length > 0 && sessions.every(s => (Number(s.spacesAvailable) || 0) <= soldOutThreshold)
      )
      .map(([ts]) => new Date(Number(ts)))
      .sort((a, b) => a - b);
});

const showSoldOutDatesObject = computed(() => ({
  enabled: !!props.showSoldOutDatesOnCalendar,
  dates: props.showSoldOutDatesOnCalendar ? soldOutDates.value : [],
}));

////////////////////////////
// Data fetch functions
////////////////////////////
const fetchEvents = async () => {
  await fetch(
      buildUri('/api/digitickets/events', {
        branchId: props.branchId,
        eventIds: customEventIds.value || props.eventIds,
      })
  )
      .then((res) => res.json())
      .then((e) => {
        loading.events = false;
        events.value = e.map((e) => ({
          ...e,
          sessions: [],
        }))
      });
}

const fetchSessions = async () => {
  await fetch(
      buildUri('/api/digitickets/sessions', {
        eventIds: customEventIds.value || props.eventIds,
        branchId: props.branchId,
      })
  )
      .then((res) => res.json())
      .then((sessions) => {
        loading.sessions = false;
        sessions.forEach((s) => {
          events.value.find(
              (e) => e.eventCatID === s.eventCatID
          )?.sessions.push(s);
        })
      })
}

const fetchTickets = async () => {
  await fetch(
      buildUri('/api/digitickets/tickets', {
        eventIds: customEventIds.value || props.eventIds,
        branchId: props.branchId,
      })
  )
      .then((res) => res.json())
      .then((t) => {
        loading.tickets = false;
        tickets.value = t;
      })
}

////////////////////////////
// Helper functions
////////////////////////////

/**
 * Nav levels:
 *  0: Starting level, only the starting calendar is shown with all available sessions
 *  1: A date has been selected. A date-based event may also have been chosen
 *  2: A time-based event has been selected and the user is choosing a session time
 */
const goBack = () => {
  if (navLevel.value === 1) {
    selectedDate.value = null;
    selectedEvent.value = null;
    navLevel.value--;

    return;
  }

  if (navLevel.value === 2) {
    choosingSession.value = false
    selectedSession.value = null;
    navLevel.value--;
  }
}

const handleBookingClick = () => {
  if (loadingAvailability.value) {
    return;
  }
  if (!selectedEventRequiresTimePicker) {
    return;
  }
  choosingSession.value = true;
  navLevel.value = 2;
}

const selectDate = (cell) => {
  if (!sessionDates.value.find((d) => moment(d).isSame(moment(cell.date), 'date'))) {
    return;
  }

  selectedDate.value = cell.date;
  navLevel.value = 1;
}

const selectEvent = (event) => {
  selectedEvent.value = event;

  // Triggers API sales limit availability check
  if ((props.checkSalesLimitEnabled) && (selectedDate.value && event?.eventCatID)) {
    fetchSessionAvailability(selectedDate.value, event.eventCatID);
  }

  // If the selected session is time based or has multiple sessions for the date
  // then we won't set the selected session just yet. Otherwise, set the selected
  // session as the only one available for this date.
  if (event.sessionType === 'Time-based' || event.sessions.length > 1) {
    selectedSession.value = null;

    return;
  }

  selectedSession.value = event.sessions[0];
}

const close = () => {
  model.value = false;
  selectedDate.value = null;
  selectedEvent.value = null;
  selectedSession.value = null;
  choosingSession.value = false;
  navLevel.value = 0;
  customEventIds.value = null;
}

const openOffcanvas = (eventIds = null) => {
  customEventIds.value = eventIds;
  model.value = true;
}

// CHECK AVAILABILITY AGAINST SALES LIMIT
// Store results of call to controller
const sessionAvailabilityMap = ref({});

// Fetch session availability
async function fetchSessionAvailability(date, eventCatID) {
  if (!eventCatID || !date) return;

  const formattedDate = moment(date).format('YYYY-MM-DD');

  sessionAvailabilityMap.value = {}; // clear old results in case of repeat use

  loadingAvailability.value = true;

  try {
    const response = await fetch('/api/digitickets/session-availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventCatID,
        date: formattedDate,
      }),
    });

    const result = await response.json();
    sessionAvailabilityMap.value = result.availability || {};

  } catch (err) {
    console.error('Failed to fetch session availability:', err);
    sessionAvailabilityMap.value = {};

  } finally {
    loadingAvailability.value = false;
  }
}

const onDateChange = (newDate) => {
  if (selectedEvent.value?.eventCatID && props.checkSalesLimitEnabled) {
    fetchSessionAvailability(newDate, selectedEvent.value.eventCatID);
  }
};

// HELPER FUNCTION FOR MILKY WAY ONLY
// PASSES TICKET CATEGORY ID ONLY IF SELECTED EVENT IS 'PARK ENTRY'

const bookingHref = computed(() => {
  if (!selectedSession.value) {
    return null;
  }

  const baseHref = bookingLink(
      selectedSession.value.sessionID,
      props.cfDomain,
      props.branchId
  );

  return String(selectedEvent.value?.eventCatID) === '64556'
      ? `${baseHref}&catID=61808`
      : baseHref;
});



</script>

<style scoped lang="scss">
.offcanvas-header {
  position: relative;

  .btn-close {
    position: absolute;
    padding: 15px;
    --bs-btn-close-opacity: 1;
  }

  .btn-close:not(.back) {
    right: 15px;
    background: url("../assets/icons/circle-xmark-light.svg") no-repeat center center;
    background-size: 30px 30px;
  }
}
.back {
  --bs-btn-close-bg: none;
  left: 5px;

  i {
    top: -4px;
    position: relative;
    font-size: 22px; color: #a5a5a5
  }
}

.powered-by {
  height: 20px;   margin-top: 20px;
}

.text-uppercase {
  text-transform: uppercase;
}

.text-capitalize {
  text-transform: capitalize;
}

.footer {
  box-shadow: 0px -2px 6px 0px rgba(0, 0, 0, 0.08);
  //padding: var(--bs-offcanvas-padding-y) var(--bs-offcanvas-padding-x);


  .booking-date-button {
    box-shadow: 0px -8px 20px #00000014;
    padding: 16px;
    position: absolute;
    bottom: 100%;
    background: #fff;
    font-size: 1.125rem;
    left: 0;
    width: 100%;

    .confirmation {
      font-weight: bold;
    }

    .btn.btn-secondary {
      width: 100%;
      margin: 0 !important;
      border-radius: 0;
      padding: 13px 20px;
      background: #000;
      font-weight: bold;
      &:hover {
        background: #DADADA; color: #fff !important;
      }
      &.disabled {
        background: #ccc !important; color: #fff !important;
      }
    }
  }
}
</style>

<style lang="scss">

.offcanvas.digi-booking {
  &.offcanvas-bottom {
    height: 95%;
    border-radius: 16px 16px 0 0;
  }

  &.offcanvas-end {
    width: 500px;
  }

  .offcanvas-header {
    border-bottom: 1px solid #D3D3D3;
  }

  .offcanvas-body {
    padding-bottom: 90px ;
  }

  color: #000;
  text-align: center;
  font-family: 'Arial', Sans-Serif, serif;
  font-size: 16px;
  font-style: normal;
  line-height: 147%;

  h5 {
    font-weight: 700;
    font-size: 16px;
  }

  .btn { border-radius: 0;
    &.btn-default {
      padding: 15px 20px;
      font-weight: bold;
      &:hover {
        background: var(--button-hover) !important; color: var(--button-hover-color) !important;
      }
    }
  }
  .border-secondary {
    border: 1px solid #d9d9d9 !important;
    &.border-2 { border-width: 2px !important;}
  }
  // Scss styles are affecting some widget elements.
  // This is intended to reset them to widget-only styles
  .card-title {
    font-family: unset !important;
    font-size: 18px !important;
    color: #000000 !important;
    margin: 0 0 8px !important;
  }
  .card-body {
    background-color: unset !important;
    color: #000000 !important;
    position: unset !important;
    top: 0px;
    margin: 0 !important;
  }
  h5 { font-family: unset; }
  .btn {
    font-family: unset;
    text-align: center;
  }
}

// Carl's Custom Colours
.digi-booking {
  .text-success { color: #1E824C !important;}
  .bg-success { background-color: #1E824C !important;}
  .text-warning { color: #CC8705 !important; }
  .bg-warning { background-color: #FDB836 !important; }
  .text-danger { color: #DD303F !important; }
  .bg-danger { background-color: #DD303F !important; }
}

</style>